// Ported verbatim from obsidian-plugin/src/utils/graphMerge.ts.
// Framework-agnostic helpers for merging retrieval graph events
// into a long-lived accumulated investigation subgraph.
import type {
  AccumulatedGraph,
  AccumulatedNode,
  AccumulatedEdge,
  GraphTraversalEvent,
  SourceDoc,
} from '../types';

export function emptyAccumulatedGraph(): AccumulatedGraph {
  return { nodes: [], edges: [], queryCount: 0, lastUpdated: Date.now() };
}

function edgeKey(source: string, relType: string, target: string): string {
  return `${source}--${relType}--${target}`;
}

export function mergeGraphEvent(
  current: AccumulatedGraph,
  incoming: GraphTraversalEvent,
  queryIndex: number,
  queryMessageId: string,
  timestamp: number,
): AccumulatedGraph {
  const nodeMap = new Map<string, AccumulatedNode>();
  for (const n of current.nodes) {
    nodeMap.set(n.id, { ...n });
  }

  const edgeMap = new Map<string, AccumulatedEdge>();
  for (const e of current.edges) {
    edgeMap.set(e.id, { ...e });
  }

  for (const n of incoming.nodes) {
    const existing = nodeMap.get(n.id);
    if (existing) {
      // Update existing node — never overwrite pinned or position
      existing.lastSeenAtQueryIndex = queryIndex;
      if (!existing.sourceQueryIds.includes(queryMessageId)) {
        existing.sourceQueryIds = [...existing.sourceQueryIds, queryMessageId];
      }
      if (n.score !== undefined) existing.score = n.score;
      if (n.strategy !== undefined) existing.strategy = n.strategy;
      if (n.properties) existing.properties = { ...existing.properties, ...n.properties };
    } else {
      nodeMap.set(n.id, {
        id: n.id,
        label: n.label,
        name: n.name,
        score: n.score,
        strategy: n.strategy,
        properties: n.properties,
        addedAtQueryIndex: queryIndex,
        lastSeenAtQueryIndex: queryIndex,
        addedAtTimestamp: timestamp,
        sourceQueryIds: [queryMessageId],
        pinned: false,
      });
    }
  }

  for (const e of incoming.edges) {
    const source = e.source;
    const target = e.target;
    const key = edgeKey(source, e.rel_type, target);

    const existing = edgeMap.get(key);
    if (existing) {
      if (!existing.sourceQueryIds.includes(queryMessageId)) {
        existing.sourceQueryIds = [...existing.sourceQueryIds, queryMessageId];
      }
    } else {
      edgeMap.set(key, {
        id: key,
        source,
        target,
        rel_type: e.rel_type,
        data_source: e.data_source,
        addedAtQueryIndex: queryIndex,
        sourceQueryIds: [queryMessageId],
      });
    }
  }

  return {
    nodes: Array.from(nodeMap.values()),
    edges: Array.from(edgeMap.values()),
    queryCount: queryIndex + 1,
    lastUpdated: timestamp,
  };
}

/** Merge neighbor-expansion results into the accumulated graph. */
export function mergeNeighborExpansion(
  current: AccumulatedGraph,
  centerNodeId: string,
  nodes: { id: string; label: string; name: string; score?: number; strategy?: string; properties?: Record<string, unknown> }[],
  edges: { source: string; target: string; rel_type: string; data_source?: string }[],
): AccumulatedGraph {
  const nodeMap = new Map<string, AccumulatedNode>();
  for (const n of current.nodes) {
    nodeMap.set(n.id, { ...n });
  }

  const edgeMap = new Map<string, AccumulatedEdge>();
  for (const e of current.edges) {
    edgeMap.set(e.id, { ...e });
  }

  const qi = current.queryCount;
  const ts = Date.now();

  for (const n of nodes) {
    const existing = nodeMap.get(n.id);
    if (existing) {
      if (n.properties) {
        existing.properties = { ...existing.properties, ...n.properties };
      }
    } else {
      nodeMap.set(n.id, {
        id: n.id,
        label: n.label as AccumulatedNode['label'],
        name: n.name,
        score: n.score,
        strategy: n.strategy,
        properties: n.properties,
        addedAtQueryIndex: qi,
        lastSeenAtQueryIndex: qi,
        addedAtTimestamp: ts,
        sourceQueryIds: [`expand:${centerNodeId}`],
        pinned: false,
      });
    }
  }

  for (const e of edges) {
    const key = edgeKey(e.source, e.rel_type, e.target);
    if (!edgeMap.has(key)) {
      edgeMap.set(key, {
        id: key,
        source: e.source,
        target: e.target,
        rel_type: e.rel_type,
        data_source: e.data_source,
        addedAtQueryIndex: qi,
        sourceQueryIds: [`expand:${centerNodeId}`],
        expanded: true,
      });
    }
  }

  return {
    nodes: Array.from(nodeMap.values()),
    edges: Array.from(edgeMap.values()),
    queryCount: current.queryCount,
    lastUpdated: ts,
  };
}

/** Wrap a SourceDoc search result as a synthetic graph event and merge it. */
export function mergeSearchResult(
  current: AccumulatedGraph,
  doc: SourceDoc,
): AccumulatedGraph {
  const event: GraphTraversalEvent = {
    nodes: [
      {
        id: doc.cite_key,
        label: 'Document',
        name: doc.cite_key,
        score: doc.score,
        strategy: 'manual_search',
      },
    ],
    edges: [],
  };
  return mergeGraphEvent(current, event, current.queryCount, `search:${doc.cite_key}`, Date.now());
}

/** Extract seed_entities and scope_cite_keys from pinned (or all) nodes. */
export function deriveContextFromGraph(
  graph: AccumulatedGraph,
  scope: 'none' | 'search_within' | 'expand_from',
): { seed_entities: string[]; scope_cite_keys: string[] } {
  if (scope === 'none') return { seed_entities: [], scope_cite_keys: [] };
  const pinned = graph.nodes.filter(n => n.pinned);
  const selected = pinned.length > 0 ? pinned : graph.nodes;
  const seed_entities: string[] = [];
  const scope_cite_keys: string[] = [];
  for (const node of selected) {
    if (node.label === 'Document') scope_cite_keys.push(node.name);
    else seed_entities.push(node.name);
  }
  // search_within: constrain to pinned docs + entities
  // expand_from: only inject entities, don't restrict docs
  return {
    seed_entities: seed_entities.slice(0, 5),
    scope_cite_keys: scope === 'search_within' ? scope_cite_keys : [],
  };
}

/** Remove all non-pinned nodes and their edges. */
export function clearUnpinned(graph: AccumulatedGraph): AccumulatedGraph {
  const pinnedNodes = graph.nodes.filter((n) => n.pinned);
  const pinnedIds = new Set(pinnedNodes.map((n) => n.id));
  return {
    nodes: pinnedNodes,
    edges: graph.edges.filter((e) => pinnedIds.has(e.source) && pinnedIds.has(e.target)),
    queryCount: graph.queryCount,
    lastUpdated: Date.now(),
  };
}
